import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import '../styles/HeroSection.css';

const HERO_IMAGES = [
  '/images/hero/hero1.webp',
  '/images/hero/hero2.webp',
  '/images/hero/hero3.webp',
  '/images/hero/hero4.webp',
  '/images/hero/hero5.webp',
];

const preloadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(src);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex === HERO_IMAGES.length - 1 ? 0 : prevIndex + 1;
        setPreviousIndex(prevIndex);
        return nextIndex;
      });
    }, 6500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (previousIndex === null || previousIndex === currentIndex) return undefined;
    const timer = window.setTimeout(() => setPreviousIndex(null), 1500);
    return () => window.clearTimeout(timer);
  }, [previousIndex, currentIndex]);

  useEffect(() => {
    const nextIndex = (currentIndex + 1) % HERO_IMAGES.length;
    const timer = window.setTimeout(() => {
      preloadImage(HERO_IMAGES[nextIndex]);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [currentIndex]);

  const visibleIndices = useMemo(() => {
    const indices = new Set([currentIndex]);
    if (previousIndex !== null) indices.add(previousIndex);
    return [...indices];
  }, [currentIndex, previousIndex]);

  const goToPrev = () => {
    const nextIndex = currentIndex === 0 ? HERO_IMAGES.length - 1 : currentIndex - 1;
    setPreviousIndex(currentIndex);
    setCurrentIndex(nextIndex);
  };

  const goToNext = () => {
    const nextIndex = currentIndex === HERO_IMAGES.length - 1 ? 0 : currentIndex + 1;
    setPreviousIndex(currentIndex);
    setCurrentIndex(nextIndex);
  };

  return (
    <section className="hero" aria-label="Damtua Inn hero">
      <div className="hero-slides">
        {visibleIndices.map((idx) => (
          <img
            key={HERO_IMAGES[idx]}
            src={HERO_IMAGES[idx]}
            alt=""
            className={`hero-slide${idx === currentIndex ? ' is-active' : ''}`}
            aria-hidden={idx !== currentIndex}
            fetchPriority={idx === 0 && currentIndex === 0 ? 'high' : 'low'}
            loading={idx === 0 ? 'eager' : 'lazy'}
            decoding="async"
            width={1600}
            height={1067}
            sizes="100vw"
          />
        ))}
      </div>

      <div className="hero-veil" />

      <div className="hero-container">
        <div className="hero-meta">
          <span className="hero-meta-label">Alikadam · Bandarban · Bangladesh</span>
        </div>

        <div className="hero-content">
          <span className="hero-eyebrow">Est. 2020 · A Hillside Retreat</span>
          <h1 className="hero-title">
            <span className="hero-title-line">A quiet horizon,</span>
            <span className="hero-title-line italic">found in the hills.</span>
          </h1>
          <p className="hero-description">
            Damtua Inn is a slow, intimate sanctuary tucked into the mist of Alikadam —
            where considered rooms, regional cuisine, and unhurried days meet.
          </p>
          <div className="hero-actions">
            <Link to="/booking" className="hero-btn hero-btn-primary">
              Reserve a Stay
            </Link>
            <Link to="/rooms" className="hero-btn hero-btn-ghost">
              Discover Rooms
              <span className="hero-btn-arrow" aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        <div className="hero-footer">
          <div className="hero-pagination">
            <span className="hero-pagination-current">
              {String(currentIndex + 1).padStart(2, '0')}
            </span>
            <span className="hero-pagination-rule" aria-hidden="true" />
            <span className="hero-pagination-total">
              {String(HERO_IMAGES.length).padStart(2, '0')}
            </span>
          </div>

          <div className="hero-controls" role="group" aria-label="Hero gallery controls">
            <button
              type="button"
              className="hero-arrow"
              onClick={goToPrev}
              aria-label="Previous image"
            >
              <span aria-hidden="true">←</span>
            </button>
            <button
              type="button"
              className="hero-arrow"
              onClick={goToNext}
              aria-label="Next image"
            >
              <span aria-hidden="true">→</span>
            </button>
          </div>

          <a className="hero-scroll" href="#main-content-anchor" aria-label="Scroll down">
            <span>Scroll</span>
            <span className="hero-scroll-line" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
